import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material'
import { Layout } from 'components/Layout/Layout'
import { ThemeProvider as AppThemeProvider, useTheme } from 'context/ThemeContext'
import { useUser } from 'hooks/user'
import Dashboard from 'pages/Dashboard/Dashboard'
import Facility from 'pages/Facility/Facility'
import FacilityList from 'pages/FacilityList/FacilityList'
import Mission from 'pages/Mission/Mission'
import MissionControl from 'pages/MissionControl/MissionControl'
import MissionList from 'pages/MissionList/MissionList'
import Account from 'pages/Account/Account'
import AccountFavorites from 'pages/Account/AccountFavorites'
import AccountNotificationsHistory from 'pages/Account/AccountNotificationsHistory'
import AccountProfile from 'pages/Account/AccountProfile'
import AccountOrganizations from 'pages/Account/AccountOrganizations'
import AccountUsers from 'pages/Account/AccountUsers'
import NotificationSettings from 'pages/NotificationSettings/NotificationSettings'
import NotFound from 'pages/NotFound/NotFound'
import RecordedRouteList from 'pages/RecordedRouteList/RecordedRouteList'
import Robot from 'pages/Robot/Robot'
import Assets from 'pages/Assets/Assets'
import RobotMapList from 'pages/RobotMapList/RobotMapList'
import Scenario from 'pages/Scenario/Scenario'
import ScenarioList from 'pages/ScenarioList/ScenarioList'
import NewPassword from 'pages/Signin/NewPassword/NewPassword'
import RestorePassword from 'pages/Signin/RestorePassword/RestorePassword'
import Signin from 'pages/Signin/Signin'
import Station from 'pages/Station/Station'
import Scheduler from 'pages/TaskScheduler/TaskScheduler'
import TaskSchedulerNormalized from 'pages/TaskSchedulerNormalized/TaskSchedulerNormalized'
import { useEffect, useMemo } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'

import { PATH } from './utils/constants'

const getTheme = (mode: 'light' | 'dark') =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: '#2E70EF',
      },
      secondary: {
        main: mode === 'light' ? '#000A19CC' : '#f5f5f7',
      },
      background: {
        default: 'transparent',
        paper: mode === 'light' ? '#ffffff' : 'rgba(34, 34, 26, 0.85)',
      },
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  })

function PrivatePage({ children }: { children: JSX.Element }) {
  const user = useUser()
  const navigate = useNavigate()

  useEffect(() => {
    if (!user?.state.userLoggedIn) {
      navigate(PATH.SIGNIN)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.state.userLoggedIn])

  return children
}

function AuthPage({ children }: { children: JSX.Element }) {
  const location = useLocation()
  const user = useUser()

  if (user?.state.userLoggedIn) {
    return <Navigate to={PATH.ASSET_LIST} state={{ from: location }} />
  }

  return children
}

function AppContent() {
  const { theme } = useTheme()
  const muiTheme = useMemo(() => getTheme(theme), [theme])

  return (
    <MuiThemeProvider theme={muiTheme}>
      <BrowserRouter>
        <Routes>
          <Route element={<Layout.PublicPage />}>
            <Route path={PATH.SIGNIN} element={<AuthPage children={<Signin />} />} />
            <Route path={PATH.RESTORE_PASSWORD} element={<AuthPage children={<RestorePassword />} />} />
            <Route path={PATH.NEW_PASSWORD} element={<AuthPage children={<NewPassword />} />} />
            <Route path={PATH.NOT_FOUND} element={<NotFound />} />
          </Route>
          <Route element={<Layout.FullscreenPage />}>
            <Route path={PATH.MISSION_ID} element={<PrivatePage children={<Mission />} />} />
            <Route path={PATH.ROBOT_ID} element={<PrivatePage children={<Robot />} />} />
            <Route path={PATH.STATION_ID} element={<PrivatePage children={<Station />} />} />
            <Route path={PATH.FACILITY_ID} element={<PrivatePage children={<Facility />} />} />
            <Route path={PATH.SCENARIO_ID} element={<PrivatePage children={<Scenario />} />} />
            <Route path={PATH.MISSION_LIST} element={<PrivatePage children={<MissionList />} />} />
            <Route path={PATH.STATION_LIST} element={<Navigate to={`${PATH.ASSET_LIST}?tab=stations`} replace />} />
            <Route path={PATH.RECORDED_ROUTE_LIST} element={<PrivatePage children={<RecordedRouteList />} />} />
            <Route path={PATH.FACILITY_LIST} element={<PrivatePage children={<FacilityList />} />} />
            <Route path={PATH.ANALYTICS} element={<PrivatePage children={<Dashboard useNormalizedApi />} />} />
            <Route path={PATH.ASSET_LIST} element={<PrivatePage children={<Assets />} />} />
            <Route path={PATH.MISSION_CONTROL} element={<PrivatePage children={<MissionControl />} />} />
            <Route path={PATH.SCENARIO_LIST} element={<PrivatePage children={<ScenarioList />} />} />
            <Route path={PATH.CALENDAR} element={<PrivatePage children={<TaskSchedulerNormalized />} />} />
            <Route path={PATH.CALENDAR_NORMALIZED} element={<Navigate to={PATH.CALENDAR} replace />} />
            <Route path={PATH.CALENDAR_LEGACY} element={<PrivatePage children={<Scheduler />} />} />
            <Route path={PATH.MAP_LIST} element={<PrivatePage children={<RobotMapList />} />} />
            <Route path={PATH.ACCOUNT} element={<PrivatePage children={<Account />} />}>
              <Route path="profile" element={<AccountProfile />} />
              <Route path="users" element={<AccountUsers />} />
              <Route path="organizations" element={<AccountOrganizations />} />
              <Route path="notifications" element={<AccountNotificationsHistory />} />
              <Route path="notification-settings" element={<NotificationSettings />} />
              <Route path="favorites" element={<AccountFavorites />} />
            </Route>
            <Route
              path={PATH.NOTIFICATION_SETTINGS}
              element={<Navigate to={PATH.ACCOUNT_NOTIFICATION_SETTINGS} replace />}
            />
          </Route>
          <Route element={<Layout.MainPage />}>
            <Route path={PATH.ANALYTICS_LEGACY} element={<PrivatePage children={<Dashboard />} />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </MuiThemeProvider>
  )
}

function App() {
  return (
    <AppThemeProvider>
      <AppContent />
    </AppThemeProvider>
  )
}

export default App
